import React, { useState, useEffect } from 'react';

const RegistrationManager = ({ tournamentId }) => {
  const [tournament, setTournament] = useState(null);
  const [event, setEvent] = useState(null);
  const [players, setPlayers] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);

  
  // Form state
  const [selectedPlayerId, setSelectedPlayerId] = useState('');
  const [playerSearchTerm, setPlayerSearchTerm] = useState(''); // NEW
  const [filteredPlayers, setFilteredPlayers] = useState([]); // NEW
  const [showPlayerDropdown, setShowPlayerDropdown] = useState(false); // NEW
  const [newPlayerFirstName, setNewPlayerFirstName] = useState('');
  const [newPlayerLastName, setNewPlayerLastName] = useState('');
  const [newPlayerPhone, setNewPlayerPhone] = useState('');
  const [newPlayerEmail, setNewPlayerEmail] = useState('');
  const [collectAddon, setCollectAddon] = useState(false);
  const [addonAmount, setAddonAmount] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [balanceSuggestion, setBalanceSuggestion] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  // NEW: LPS state
  const [collectLPS, setCollectLPS] = useState(false);
  const [lpsAmount, setLpsAmount] = useState('');

  const getApiUrl = () => {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:3000';
    } else if (hostname === '10.0.0.28') {
      return `https://${hostname}/api`;
    } else {
      return `https://${hostname}/api`;
    }
  };

  useEffect(() => {
    loadData();
  }, [tournamentId]);

  // NEW: Filter players based on search term
  useEffect(() => {
    if (selectedPlayerId) return;   // a player is chosen — don't reopen the dropdown
    if (playerSearchTerm.length > 0) {
      const filtered = players.filter(player => {
        const fullName = `${player.first_name} ${player.last_name}`.toLowerCase();
        return fullName.includes(playerSearchTerm.toLowerCase());
      });
      setFilteredPlayers(filtered);
      setShowPlayerDropdown(true);
      setHighlightedIndex(-1);   // reset highlight on new search
    } else {
      setFilteredPlayers([]);
      setShowPlayerDropdown(false);
      setHighlightedIndex(-1); 
    }
  }, [playerSearchTerm, players]);

  const loadData = async () => {
    try {
      // Load tournament
      const tournamentResponse = await fetch(`${getApiUrl()}/tournaments/${tournamentId}`);
      const tournamentData = await tournamentResponse.json();
      setTournament(tournamentData);

      // Load event
      const eventResponse = await fetch(`${getApiUrl()}/events/${tournamentData.event_id}`);
      const eventData = await eventResponse.json();
      setEvent(eventData);
      
      // Set LPS and Addon amounts from event settings
      if (eventData.lps_fee) {
        setLpsAmount(eventData.lps_fee);
      }
      if (eventData.addon_amount) {
        setAddonAmount(eventData.addon_amount);
      }

      // Load players
      const playersResponse = await fetch(`${getApiUrl()}/players`);
      const playersData = await playersResponse.json();
      setPlayers(playersData);

      // Load registrations
      const registrationsResponse = await fetch(
        `${getApiUrl()}/tournaments/${tournamentId}/registrations`
      );
      const registrationsData = await registrationsResponse.json();
      setRegistrations(registrationsData);

      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (!showPlayerDropdown || filteredPlayers.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev =>
        prev < filteredPlayers.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev =>
        prev > 0 ? prev - 1 : filteredPlayers.length - 1
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // If something is highlighted, pick it; otherwise pick the first result
      const choice = highlightedIndex >= 0
        ? filteredPlayers[highlightedIndex]
        : filteredPlayers[0];
      if (choice) selectPlayer(choice);
    } else if (e.key === 'Escape') {
      setShowPlayerDropdown(false);
      setHighlightedIndex(-1);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // NEW: Select player from dropdown
  const selectPlayer = (player) => {
    setSelectedPlayerId(player.id);
    setPlayerSearchTerm(`${player.first_name} ${player.last_name}`);
    setShowPlayerDropdown(false);
    setHighlightedIndex(-1);
  };

  // NEW: Clear player selection
  const clearPlayerSelection = () => {
    setSelectedPlayerId('');
    setPlayerSearchTerm('');
    setShowPlayerDropdown(false);
  };

  const registerPlayer = async () => {
    try {
      let playerId = selectedPlayerId;

      // Create new player if needed
      if (!playerId && (newPlayerFirstName || newPlayerLastName)) {
        const newPlayerResponse = await fetch(`${getApiUrl()}/players`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
          body: JSON.stringify({
            first_name: newPlayerFirstName,
            last_name: newPlayerLastName,
            phone: newPlayerPhone,
            email: newPlayerEmail
          })
        });

        if (!newPlayerResponse.ok) {
          throw new Error('Failed to create player');
        }

        const newPlayer = await newPlayerResponse.json();
        playerId = newPlayer.id;
      }

      if (!playerId) {
        alert('Please select or create a player');
        return;
      }

      // MODIFIED: Register player with LPS info
      const registrationData = {
        player_id: playerId,
        tournament_id: tournamentId,
        lps_paid: collectLPS,
        lps_amount: collectLPS ? parseFloat(event.lps_fee) || 0 : 0,
        lps_active: collectLPS,
        addon_paid: collectAddon,
        addon_paid_amount: collectAddon ? parseFloat(event.addon_amount) || 0 : 0
      };

      // ADD THESE DEBUG LOGS:
      console.log('=== REGISTRATION DEBUG ===');
      console.log('collectAddon:', collectAddon);
      console.log('event.addon_amount:', event.addon_amount);
      console.log('Full registrationData:', registrationData);
      console.log('=== END DEBUG ===');

      const response = await fetch(`${getApiUrl()}/tournaments/${tournamentId}/registrations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(registrationData)
      });

      if (response.ok) {
        const data = await response.json();

        if (data.auto_seat) {
          // Only print a ticket when auto-seating produced a seat or alternate status
          if (data.seat || data.is_alternate) {
            printReceipt({
              player_name: data.player_name || `${data.first_name || ''} ${data.last_name || ''}`.trim(),
              tournament_name: tournament?.event_name || event?.name,
              buy_in: event?.buy_in_amount,
              table_number: data.seat ? data.seat.table_number : null,
              seat_number: data.seat ? data.seat.seat_number : null,
              is_alternate: data.is_alternate || false
            });
          }

          if (data.seat) {
            alert(`Player registered!\n\nSeating: Table ${data.seat.table_number}, Seat ${data.seat.seat_number}`);
          } else if (data.is_alternate) {
            alert(`Player registered as an ALTERNATE.\n\nNo seats currently available — they will be seated when one opens.`);
          }
        } else {
          alert('Player registered successfully!');
        }

        // Reset form
        setSelectedPlayerId('');
        clearPlayerSelection(); // NEW
        setNewPlayerFirstName('');
        setNewPlayerLastName('');
        setNewPlayerPhone('');
        setNewPlayerEmail('');
        setCollectLPS(false); // NEW

        loadData();
      } else {
        const error = await response.json();
        alert(`Error: ${error.error || 'Registration failed'}`);
      }
    } catch (error) {
      console.error('Error registering player:', error);
      alert('Failed to register player');
    }
  };

  const printReceipt = (data) => {
    // data: { player_name, tournament_name, buy_in, table_number, seat_number, is_alternate }
    const now = new Date();
    const dateStr = now.toLocaleString();

    const seatingHtml = data.is_alternate
      ? `<div class="alt">ALTERNATE<br/><span class="small">Seat assigned when available</span></div>`
      : (data.table_number != null && data.seat_number != null)
        ? `<div class="seat">Table ${data.table_number} &nbsp;·&nbsp; Seat ${data.seat_number}</div>`
        : '';

    const html = `
      <html>
        <head>
          <title>Receipt</title>
          <style>
            @media print { @page { size: 80mm auto; margin: 0; } }
            body {
              width: 80mm;
              margin: 0;
              padding: 6mm 4mm;
              font-family: 'Courier New', monospace;
              color: #000;
              text-align: center;
            }
            .logo { max-width: 60mm; margin-bottom: 4mm; }
            .tname { font-size: 14pt; font-weight: bold; margin: 2mm 0; }
            .player { font-size: 12pt; margin: 2mm 0; }
            .buyin { font-size: 12pt; margin: 3mm 0; }
            .seat { font-size: 16pt; font-weight: bold; margin: 4mm 0; border-top: 1px dashed #000; border-bottom: 1px dashed #000; padding: 3mm 0; }
            .alt { font-size: 15pt; font-weight: bold; margin: 4mm 0; border-top: 1px dashed #000; border-bottom: 1px dashed #000; padding: 3mm 0; }
            .small { font-size: 9pt; font-weight: normal; }
            .meta { font-size: 9pt; margin-top: 4mm; color: #000; }
          </style>
        </head>
        <body>
          <img src="/receipt-logo.png" class="logo" alt="" />
          <div class="tname">${data.tournament_name || ''}</div>
          <div class="player">${data.player_name || ''}</div>
          <div class="buyin">Buy-in: $${Number(data.buy_in || 0).toFixed(2)}</div>
          ${seatingHtml}
          <div class="meta">${dateStr}</div>
        </body>
      </html>
    `;

    // Open a hidden iframe, write the receipt, print, then clean up
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();

    // Wait for the logo image to load before printing, so it isn't blank
    const img = doc.querySelector('img');
    const doPrint = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      // Remove the iframe after a short delay
      setTimeout(() => document.body.removeChild(iframe), 1000);
    };
    if (img && !img.complete) {
      img.onload = doPrint;
      img.onerror = doPrint; // print even if logo fails, don't block
    } else {
      doPrint();
    }
  };

const processRebuy = async (registrationId) => {
    const reg = registrations.find(r => r.id === registrationId);
    const maxRebuys = tournament?.max_rebuys;
    const usedRebuys = reg?.rebuy_count || 0;

    // HARD BLOCK: at or over max re-entries — refuse outright
    if (maxRebuys && usedRebuys >= maxRebuys) {
      alert(
        `❌ Cannot process rebuy.\n\n` +
        `This player has used the maximum number of re-entries (${maxRebuys}).`
      );
      return;
    }

    // SOFT WARNING: past the rebuy deadline level — allow override
    const currentLevel = tournament?.current_level;
    const deadline = tournament?.rebuy_deadline_level;
    const pastDeadline = deadline && currentLevel && currentLevel > deadline;

    let message;
    if (pastDeadline) {
      message =
        `⚠️ The rebuy period has ended ` +
        `(deadline was level ${deadline}, now on level ${currentLevel}).\n\n` +
        `Process this rebuy anyway?`;
    } else if (reg?.status === 'eliminated') {
      // Normal re-entry — eliminated but within the rules
      const remaining = maxRebuys ? ` (${maxRebuys - usedRebuys} re-entr${maxRebuys - usedRebuys === 1 ? 'y' : 'ies'} remaining)` : '';
      message = `Re-enter this eliminated player with a rebuy?${remaining}`;
    } else {
      // Active player taking a normal rebuy
      message = 'Process rebuy for this player?';
    }

    if (!window.confirm(message)) return;

    try {
      const response = await fetch(
        `${getApiUrl()}/tournaments/${tournamentId}/registrations/${registrationId}/rebuy`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );

      if (response.ok) {
        alert('Rebuy processed');

        if (response.ok) {
        const data = await response.json();
        alert('Rebuy processed');

        // If this was a re-entry that got auto-seated, print a receipt
        if (data.is_reentry && (data.seat || data.is_alternate)) {
          printReceipt({
            player_name: data.player_name,
            tournament_name: data.tournament_name,
            buy_in: data.buy_in,
            table_number: data.seat ? data.seat.table_number : null,
            seat_number: data.seat ? data.seat.seat_number : null,
            is_alternate: data.is_alternate || false
          });
        }

        loadData();
      }
        loadData();
      } else {
        const error = await response.json();
        alert(`Error: ${error.error || 'Rebuy failed'}`);
      }
    } catch (error) {
      console.error('Error processing rebuy:', error);
      alert('Failed to process rebuy');
    }
  };

  const checkBalance = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/tournaments/${tournamentId}/balance-suggestion`);
      const data = await response.json();
      setBalanceSuggestion(data);
    } catch (error) {
      console.error('Error checking balance:', error);
    }
  };  

const processAddon = async (registrationId) => {
    const reg = registrations.find(r => r.id === registrationId);
    const maxAddons = tournament?.max_addons;
    const usedAddons = reg?.addon_count || 0;

    // HARD BLOCK: at or over max addons — refuse outright
    if (maxAddons && usedAddons >= maxAddons) {
      alert(
        `❌ Cannot process addon.\n\n` +
        `This player has used the maximum number of addons (${maxAddons}).`
      );
      return;
    }

    // SOFT WARNING: past the addon level + 1 grace — allow override
    const currentLevel = tournament?.current_level;
    const addonLevel = tournament?.addon_level;
    const pastAddonWindow = addonLevel && currentLevel && currentLevel > addonLevel + 1;

    let message;
    if (pastAddonWindow) {
      message =
        `⚠️ The addon period has passed ` +
        `(addon level was ${addonLevel}, now on level ${currentLevel}).\n\n` +
        `Process this addon anyway?`;
    } else {
      const remaining = maxAddons ? ` (${maxAddons - usedAddons} addon${maxAddons - usedAddons === 1 ? '' : 's'} remaining)` : '';
      message = reg?.status === 'eliminated'
        ? `Re-enter this eliminated player with an addon?${remaining}`
        : `Process addon for this player?${remaining}`;
    }

    if (!window.confirm(message)) return;

    try {
      const response = await fetch(
        `${getApiUrl()}/tournaments/${tournamentId}/registrations/${registrationId}/addon`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );

      if (response.ok) {
        alert('Addon processed');
        loadData();
      } else {
        const error = await response.json();
        alert(`Error: ${error.error || 'Addon failed'}`);
      }
    } catch (error) {
      console.error('Error processing addon:', error);
      alert('Failed to process addon');
    }
  };

  const removeRegistration = async (registrationId, playerName) => {
    const confirmed = window.confirm(
      `Permanently remove ${playerName}'s registration?\n\n` +
      `This deletes their entry along with any LPS, rebuys, and addons, ` +
      `unseats them from any table, and CANNOT be undone.`
    );
    if (!confirmed) return;

    try {
      const response = await fetch(
        `${getApiUrl()}/tournaments/${tournamentId}/registrations/${registrationId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        alert(result.message);
        loadData();
      } else {
        const error = await response.json();
        alert(`Error: ${error.error || 'Failed to remove registration'}`);
      }
    } catch (error) {
      console.error('Error removing registration:', error);
      alert('Failed to remove registration');
    }
  };

  const adjustChips = async (registrationId, currentChips) => {
    const newChips = prompt('Enter new chip count:', currentChips);
    if (!newChips || newChips === currentChips.toString()) return;

    try {
      const response = await fetch(
        `${getApiUrl()}/tournaments/${tournamentId}/registrations/${registrationId}/chips`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
          body: JSON.stringify({ chips: parseInt(newChips) })
        }
      );

      if (response.ok) {
        alert('Chip count updated');
        loadData();
      }
    } catch (error) {
      console.error('Error adjusting chips:', error);
    }
  };

const undoElimination = async (registrationId, playerName) => {
    if (!window.confirm(`Restore ${playerName} to active? This reverses their elimination.`)) return;

    try {
      const response = await fetch(
        `${getApiUrl()}/registrations/${registrationId}/un-eliminate`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );

      if (response.ok) {
        alert(`${playerName} restored to active`);
        loadData();
      } else {
        const error = await response.json();
        alert(`Error: ${error.error || 'Failed to undo elimination'}`);
      }
    } catch (error) {
      console.error('Error undoing elimination:', error);
      alert('Failed to undo elimination');
    }
  };  

const eliminatePlayer = async (registrationId) => {
    if (!window.confirm('Mark this player as eliminated?')) return;

    try {
      const response = await fetch(
        `${getApiUrl()}/registrations/${registrationId}/eliminate`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
          body: JSON.stringify({
            position_finished: null,
            prize_amount: 0,
            eliminated_by_player_id: null
          })
        }
      );

      if (response.ok) {
        alert('Player eliminated');
        loadData();
        checkBalance();
      } else {
        const error = await response.json();
        alert(`Error: ${error.error || 'Failed to eliminate'}`);
      }
    } catch (error) {
      console.error('Error eliminating player:', error);
      alert('Failed to eliminate player');
    }
  };

  // Determine whether an eliminated player is still eligible to re-enter
  const isEligibleForReentry = (reg) => {
    if (reg.status !== 'eliminated') return false;

    const currentLevel = tournament?.current_level;
    const deadline = tournament?.rebuy_deadline_level;
    const maxRebuys = tournament?.max_rebuys;
    const maxAddons = tournament?.max_addons;

    // Past the grace period? (eligible through deadline + 1)
    if (deadline && currentLevel && currentLevel > deadline + 1) {
      return false;
    }

    // Is a rebuy still available?
    const rebuyAllowed = (event?.rebuy_allowed || tournament?.rebuy_allowed);
    const rebuysRemaining = rebuyAllowed &&
      (!maxRebuys || (reg.rebuy_count || 0) < maxRebuys);

    // Is an addon still available?
    const addonAllowed = (event?.addon_allowed || tournament?.addon_allowed);
    const addonsRemaining = addonAllowed &&
      (!maxAddons || (reg.addon_count || 0) < maxAddons);

    // Eligible if at least one re-entry route remains
    return rebuysRemaining || addonsRemaining;
  };

  const filteredRegistrations = registrations.filter(reg => {
      if (statusFilter === 'all') return true;
      if (statusFilter === 'active') return reg.status === 'active';
      if (statusFilter === 'eliminated') return reg.status === 'eliminated';
      if (statusFilter === 'eligible') {
        return isEligibleForReentry(reg);
      }
      return true;
    });

  const ordinal = (n) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  const sortedRegistrations = [...filteredRegistrations].sort((a, b) => {
    if (!sortField) return 0;
    const dir = sortDir === 'asc' ? 1 : -1;

    if (sortField === 'status') {
      const statusRank = { active: 0, registered: 1, eliminated: 2 };
      const aRank = statusRank[a.status] ?? 99;
      const bRank = statusRank[b.status] ?? 99;
      if (aRank !== bRank) return (aRank - bRank) * dir;
      if (a.status === 'eliminated') {
        const aPos = a.position_finished ?? 9999;
        const bPos = b.position_finished ?? 9999;
        return (aPos - bPos) * dir;
      }
      return 0;
    }

    let aVal = a[sortField];
    let bVal = b[sortField];
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return (aVal - bVal) * dir;
    }
    return String(aVal).toLowerCase().localeCompare(String(bVal).toLowerCase()) * dir;
  });

  const thSort = { cursor: 'pointer', userSelect: 'none' };
  const sortArrow = (field, activeField, dir) =>
    activeField === field ? (dir === 'asc' ? '▲' : '▼') : '';

  if (loading) {
    return <div className="container">Loading...</div>;
  }

  return (
    <div className="container" style={{ maxWidth: '1400px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Player Registration</h1>
        <button 
          className="btn"
          onClick={() => window.location.href = `/admin/tournament/${tournamentId}/control`}
        >
          ← Back to Control Panel
        </button>
      </div>

      {/* Tournament Info */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <h3>{tournament?.tournament_name || event?.name}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          <div><strong>Status:</strong> {tournament?.status}</div>
          <div><strong>Buy-in:</strong> ${event?.buy_in_amount}</div>
          <div><strong>Registered Players:</strong> {registrations.length}</div>
          <div><strong>Active Players:</strong> {registrations.filter(r => r.status === 'active').length}</div>
          {event?.lps_enabled && (
            <div><strong>LPS Fee:</strong> ${event?.lps_fee}</div>
          )}
        </div>
      </div>

      {/* Registration Form */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <h3>Register New Player</h3>
        
        {/* MODIFIED: Searchable Player Selection */}
        <div className="form-group">
          <label>Search Existing Player:</label>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              className="form-control"
              value={playerSearchTerm}
              onChange={(e) => setPlayerSearchTerm(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              onFocus={() => playerSearchTerm && setShowPlayerDropdown(true)}
              placeholder="Start typing player name..."
            />
            {selectedPlayerId && (
              <button
                onClick={clearPlayerSelection}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '20px',
                  color: '#999',
                  padding: '0 5px'
                }}
              >
                ×
              </button>
            )}
            
            {/* NEW: Dropdown */}
            {showPlayerDropdown && filteredPlayers.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                maxHeight: '200px',
                overflowY: 'auto',
                background: 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                zIndex: 1000,
                marginTop: '2px'
              }}>
                {filteredPlayers.map((player, index) => (
                  <div
                    key={player.id}
                    onClick={() => selectPlayer(player)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    style={{
                      padding: '10px 15px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #eee',
                      background: index === highlightedIndex ? '#e9f5ff' : 'white'
                    }}
                  >
                    <strong>{player.first_name} {player.last_name}</strong>
                    {player.phone && <span style={{ color: '#666', marginLeft: '10px', fontSize: '14px' }}>({player.phone})</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
          {selectedPlayerId && (
            <small style={{ color: '#28a745', marginTop: '5px', display: 'block' }}>
              ✓ Player selected
            </small>
          )}
        </div>

        <div style={{ textAlign: 'center', margin: '20px 0', color: '#666', fontSize: '14px' }}>
          — OR CREATE NEW PLAYER —
        </div>

        {/* New Player Form */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          <div className="form-group">
            <label>First Name:</label>
            <input
              type="text"
              className="form-control"
              value={newPlayerFirstName}
              onChange={(e) => setNewPlayerFirstName(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Last Name:</label>
            <input
              type="text"
              className="form-control"
              value={newPlayerLastName}
              onChange={(e) => setNewPlayerLastName(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Phone:</label>
            <input
              type="text"
              className="form-control"
              value={newPlayerPhone}
              onChange={(e) => setNewPlayerPhone(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Email:</label>
            <input
              type="email"
              className="form-control"
              value={newPlayerEmail}
              onChange={(e) => setNewPlayerEmail(e.target.value)}
            />
          </div>
        </div>

        {/* LPS and Addon Collection */}
        {(event?.lps_enabled || event?.addon_allowed) && (
          <div style={{ marginTop: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '4px' }}>
            <h4 style={{ marginTop: 0 }}>Additional Fees</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
              
              {/* LPS */}
              {event?.lps_enabled && event?.lps_fee > 0 && (
                <div>
                  <label>
                    <input
                      type="checkbox"
                      checked={collectLPS}
                      onChange={(e) => setCollectLPS(e.target.checked)}
                    />
                    {' '}Collect Last Person Standing (LPS) Fee: <strong>${event.lps_fee}</strong>
                  </label>
                </div>
              )}

              {/* Addon */}
              {event?.addon_allowed && event?.addon_amount > 0 && (
                <div>
                  <label>
                    <input
                      type="checkbox"
                      checked={collectAddon}
                      onChange={(e) => setCollectAddon(e.target.checked)}
                    />
                    {' '}Collect Addon Fee at Registration: <strong>${event.addon_amount}</strong>
                  </label>
                </div>
              )}
            </div>
          </div>
        )}

        <button
          className="btn btn-success"
          onClick={registerPlayer}
          style={{ marginTop: '20px' }}
        >
          Register Player
        </button>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '15px', flexWrap: 'wrap' }}>
        {[
          { key: 'all', label: 'All' },
          { key: 'active', label: 'Active' },
          { key: 'eliminated', label: 'Eliminated' },
          { key: 'eligible', label: 'Eliminated · Can Re-enter' }
        ].map(tab => {
          // Count for this tab
          const count = registrations.filter(reg => {
            if (tab.key === 'all') return true;
            if (tab.key === 'active') return reg.status === 'active';
            if (tab.key === 'eliminated') return reg.status === 'eliminated';
            if (tab.key === 'eligible') {
              return isEligibleForReentry(reg);
            }
            return false;
          }).length;

          const isActive = statusFilter === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key)}
              className="btn"
              style={{
                background: isActive ? '#007bff' : '#e9ecef',
                color: isActive ? 'white' : '#333',
                fontWeight: isActive ? 'bold' : 'normal',
                padding: '6px 14px'
              }}
            >
              {tab.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Registered Players */}
      <div className="card">
        <h3>Registered Players ({registrations.length})</h3>
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th onClick={() => handleSort('player_name')} style={thSort}>
                  Player Name {sortArrow('player_name', sortField, sortDir)}
                </th>
                <th onClick={() => handleSort('status')} style={thSort}>
                  Status {sortArrow('status', sortField, sortDir)}
                </th>
                <th onClick={() => handleSort('current_chips')} style={thSort}>
                  Chips {sortArrow('current_chips', sortField, sortDir)}
                </th>
                <th onClick={() => handleSort('rebuy_count')} style={thSort}>
                  Rebuys {sortArrow('rebuy_count', sortField, sortDir)}
                </th>
                <th onClick={() => handleSort('addon_count')} style={thSort}>
                  Addons {sortArrow('addon_count', sortField, sortDir)}
                </th>
                <th onClick={() => handleSort('lps_paid')} style={thSort}>
                  LPS {sortArrow('lps_paid', sortField, sortDir)}
                </th>
                <th onClick={() => handleSort('registration_time')} style={thSort}>
                  Registration Time {sortArrow('registration_time', sortField, sortDir)}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedRegistrations.map(reg => (
                <tr key={reg.id}>
                  <td><strong>{reg.player_name}</strong></td>
                  <td>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '3px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      background: reg.status === 'active' ? '#d4edda' :
                                 reg.status === 'eliminated' ? '#f8d7da' : '#fff3cd',
                      color: reg.status === 'active' ? '#155724' :
                             reg.status === 'eliminated' ? '#721c24' : '#856404'
                    }}>
                      {reg.status === 'eliminated' && reg.position_finished
                        ? `ELIMINATED - ${ordinal(reg.position_finished)}`
                        : reg.status?.toUpperCase()}
                    </span>
                  </td>
                  <td
                    onClick={() => adjustChips(reg.id, reg.current_chips || 0)}
                    style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                    title="Click to adjust chips">
                    {reg.current_chips?.toLocaleString() || 0}
                  </td>
                  <td>{reg.rebuy_count || 0}</td>
                  <td>{reg.addon_count || 0}</td>
                  {/* NEW: LPS STATUS COLUMN */}
                  <td>
                    {reg.lps_paid ? (
                      <span style={{ 
                        color: reg.lps_active ? '#28a745' : '#dc3545',
                        fontWeight: 'bold'
                      }}>
                        {reg.lps_active ? '✓ Active' : '✗ Out'}
                      </span>
                    ) : (
                      <span style={{ color: '#999' }}>—</span>
                    )}
                  </td>
                  <td style={{ fontSize: '13px' }}>
                    {new Date(reg.registration_time).toLocaleString()}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                      {/* Active players: full in-game actions */}
                      {reg.status === 'active' && (
                        <>
                          {(event?.rebuy_allowed || tournament?.rebuy_allowed) && (
                            <button
                              className="btn"
                              onClick={() => processRebuy(reg.id)}
                              style={{ fontSize: '11px', padding: '4px 8px' }}
                            >
                              Rebuy
                            </button>
                          )}
                          {(event?.addon_allowed || tournament?.addon_allowed) &&
                           (!tournament?.max_addons || (reg.addon_count || 0) < tournament.max_addons) &&
                           (!tournament?.addon_level || !tournament?.current_level || tournament.current_level <= tournament.addon_level + 1) && (
                            <button
                              className="btn"
                              onClick={() => processAddon(reg.id)}
                              style={{ fontSize: '11px', padding: '4px 8px' }}
                            >
                              Addon
                            </button>
                          )}
                          <button
                            className="btn btn-danger"
                            onClick={() => eliminatePlayer(reg.id)}
                            style={{ fontSize: '11px', padding: '4px 8px' }}
                          >
                            Eliminate
                          </button>
                        </>
                      )}

                      {/* Eliminated players: offer re-entry if the tournament allows it */}
                      {isEligibleForReentry(reg) && (event?.rebuy_allowed || tournament?.rebuy_allowed) &&
                       (!tournament?.max_rebuys || (reg.rebuy_count || 0) < tournament.max_rebuys) && (
                        <button
                          className="btn"
                          onClick={() => processRebuy(reg.id)}
                          style={{ fontSize: '11px', padding: '4px 8px', background: '#28a745', color: 'white' }}
                        >
                          Re-enter (Rebuy)
                        </button>
                      )}
                      {isEligibleForReentry(reg) && (event?.addon_allowed || tournament?.addon_allowed) &&
                       (!tournament?.max_addons || (reg.addon_count || 0) < tournament.max_addons) && (
                        <button
                          className="btn"
                          onClick={() => processAddon(reg.id)}
                          style={{ fontSize: '11px', padding: '4px 8px', background: '#28a745', color: 'white' }}
                        >
                          Re-enter (Addon)
                        </button>
                      )}

                      {reg.status === 'eliminated' && (
                        <button
                          className="btn"
                          onClick={() => undoElimination(reg.id, reg.player_name)}
                          style={{ fontSize: '11px', padding: '4px 8px', background: '#17a2b8', color: 'white' }}
                        >
                          Undo Elimination
                        </button>
                      )}

                      {/* Remove: available for any status */}
                      <button
                        className="btn btn-danger"
                        onClick={() => removeRegistration(reg.id, reg.player_name)}
                        style={{ fontSize: '11px', padding: '4px 8px', background: '#6c1313' }}
                      >
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RegistrationManager;